import { Bot, GraduationCap, Sparkles, Star, Trophy } from 'lucide-react';
import { Button, DifficultyTile, type DifficultyColorTier } from '@chessmate/ui';
import { ROBOT_LEVELS, type RobotLevel } from '@chessmate/protocol';
import { levelLabel } from '../edition/index.js';
import { useT } from '../i18n/useT.js';

const TIER_BY_ELO: (e: number) => DifficultyColorTier = (e) =>
  e <= 800 ? 'gentle' : e <= 1600 ? 'balanced' : 'advanced';

const ICON_BY_ID: Record<string, typeof Bot> = {
  kitten: Sparkles,
  puppy: Star,
  rookie: Bot,
  student: GraduationCap,
  coach: Trophy,
};

export function RobotPicker({
  selected,
  onSelect,
  onStart,
}: {
  selected: RobotLevel;
  onSelect: (level: RobotLevel) => void;
  onStart: () => void;
}) {
  const { t } = useT('games');
  return (
    <div className="flex flex-col gap-3" data-testid="robot-picker">
      <h2 className="font-display text-h3 font-bold text-fg">{t('choose-your-robot')}</h2>
      {/* A single column below `sm` left five stacked cards filling a 390px
          screen with a wide empty gutter beside them. A 5-up grid of
          68px tiles fits the five robots in one row, so Play stays above the fold. */}
      <div
        className="grid grid-cols-5 justify-items-center gap-1 sm:flex sm:gap-2 sm:flex-row sm:justify-start"
        role="radiogroup"
        aria-label="Robot difficulty"
      >
        {ROBOT_LEVELS.map((level) => (
          <DifficultyTile
            key={level.id}
            level={level.id}
            icon={ICON_BY_ID[level.id] ?? Bot}
            label={levelLabel(level)}
            colorTier={TIER_BY_ELO(level.elo)}
            selected={selected.id === level.id}
            className="max-sm:h-[4.75rem] max-sm:w-full max-sm:min-w-0 max-sm:px-0"
            onClick={() => onSelect(level)}
          />
        ))}
      </div>
      {/* Content-width CTA on desktop (the old full-bleed stretch read as a
          progress bar, audit F3); full-width thumb target on mobile. */}
      <Button
        size="lg"
        className="w-full self-auto sm:w-auto sm:self-start"
        onClick={onStart}
        data-testid="start-robot-game"
      >
        {t('play-button')}
      </Button>
    </div>
  );
}
